/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ChangeEvent } from 'react';

import { SelectField, SelectOption, TextField } from '@coursera/cds-core';

export type SurveyDropdownOption = { id: string; text: string };

export type SurveyFieldConfig =
  | { id: string; kind: 'text' | 'email' | 'tel'; label: string; placeholder?: string; fullWidth?: boolean }
  | {
      id: string;
      kind: 'dropdown';
      label: string;
      placeholder?: string;
      fullWidth?: boolean;
      options: SurveyDropdownOption[];
    };

type BaseProps = { fullWidth?: boolean };

type TextQuestionFieldProps = BaseProps & {
  kind: 'text' | 'email' | 'tel';
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
};

type DropdownQuestionFieldProps = BaseProps & {
  kind: 'dropdown';
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  options: SurveyDropdownOption[];
};

export type SurveyQuestionFieldProps = TextQuestionFieldProps | DropdownQuestionFieldProps;

const styles = {
  field: css`
    grid-column: span 1;
  `,
  fieldFullWidth: css`
    grid-column: span 2;
  `,
};

const SurveyQuestionField = (props: SurveyQuestionFieldProps) => {
  const fieldStyle = props.fullWidth ? styles.fieldFullWidth : styles.field;

  if (props.kind === 'dropdown') {
    const { label, placeholder, value, onChange, options } = props;
    const onChangeWrapper = (e: ChangeEvent<{ value: unknown }>) => {
      const selectedOption = options.find((option) => option.id === e.target.value);
      if (selectedOption != null) {
        onChange(selectedOption.id);
      }
    };

    return (
      <SelectField
        data-testid="survey-question-field-dropdown"
        css={fieldStyle}
        value={value}
        onChange={onChangeWrapper}
        fullWidth
        aria-label={label}
        placeholder={placeholder}
      >
        {options.map((option) => (
          <SelectOption key={option.id} value={option.id}>
            {option.text}
          </SelectOption>
        ))}
      </SelectField>
    );
  }

  const { label, placeholder, value, onChange, kind } = props;
  return (
    <TextField
      data-testid="survey-question-field-text"
      css={fieldStyle}
      type={kind}
      fullWidth
      aria-label={label}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default SurveyQuestionField;
